import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { View, Text } from 'react-native'
import { FlashList } from '@shopify/flash-list'
import { ScrollView } from 'react-native-gesture-handler'
import { Redirect, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Search, SearchX, WifiOff, Receipt, Building2 } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { EmptyState } from '@/components/ui/EmptyState'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { CountryPicker } from '@/components/ui/CountryPicker'
import { TourCardSkeleton } from '@/components/tours/TourCardSkeleton'
import { TourCatalogCard } from '@/components/tours/TourCatalogCard'
import { ToursCatalogGrid, ToursCatalogGridSkeleton } from '@/components/tours/ToursCatalogGrid'
import { TourScopeFilter } from '@/components/tours/TourScopeFilter'
import { TourCustomQuoteCta } from '@/components/tours/TourCustomQuoteCta'
import { env } from '@/lib/env'
import { isIsolatedPreviewBuild } from '@/lib/runtime-build'
import { useToursCatalog, useTourHost } from '@/lib/hooks/use-tours'
import { useAuth } from '@/lib/hooks/use-auth'
import { useViewerCountry } from '@/lib/hooks/use-viewer-country'
import { useToursCountryStore } from '@/lib/stores/tours-country.store'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { useProductTelemetryScroll } from '@/lib/hooks/use-product-telemetry-scroll'
import { useSemanticColors, TEXT_STYLES, INTERACTION } from '@/lib/theme/tokens'
import { DESKTOP_SPACING } from '@/lib/theme/desktop-spacing'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { findCountry } from '@/lib/data/countries'
import {
  CATALOG_COUNTRIES,
  NATIONAL_THEME_CATEGORY_CODES,
  catalogCanvasPadX,
  catalogGridColumns,
} from '@/lib/tours/catalog-layout'
import type { TourCatalogExperience } from '@/lib/types/tours.types'

function CatalogToolbarButton({
  label,
  onPress,
  testID,
  labeled,
  children,
}: {
  label: string
  onPress: () => void
  testID: string
  labeled: boolean
  children: ReactNode
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      testID={testID}
      style={{
        minHeight: INTERACTION.minTouchTarget,
        minWidth: INTERACTION.minTouchTarget,
        paddingHorizontal: labeled ? SPACING.md : 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SPACING.xs,
        borderRadius: RADIUS.lg,
        borderWidth: labeled ? 1 : 0,
        borderColor: colors.border,
        backgroundColor: labeled ? colors.surface : 'transparent',
      }}
    >
      {children}
      {labeled ? (
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textPrimary }}
        >
          {label}
        </Text>
      ) : null}
    </Pressable>
  )
}

interface ToursCatalogScreenProps {
  routeBase?: '/tours' | '/preview'
}

export function ToursCatalogScreen({ routeBase }: ToursCatalogScreenProps) {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  // The native Preview app intentionally renders the same catalog route as
  // production so testers exercise the real navigation chrome. Its destination
  // must still stay inside the fixture route family: a missing prop used to
  // send this CTA to /tours/quote, which can invoke the production quote edge
  // function. Explicit Preview routes retain their own route base below.
  const resolvedRouteBase: '/tours' | '/preview' =
    routeBase ?? (isIsolatedPreviewBuild ? '/preview' : '/tours')
  const { user } = useAuth()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, isTablet, width, bp } = useBreakpoint()
  const editorial = isDesktop || isTablet
  const columns = catalogGridColumns(width, editorial)
  const gap = editorial ? DESKTOP_SPACING.cardGap[bp] : SPACING.md
  const padX = editorial ? catalogCanvasPadX() : SPACING.lg
  const country = useViewerCountry()
  const setCatalogCountry = useToursCountryStore((s) => s.setCountry)
  const { data, isLoading, isRefetching, error, refetch } = useToursCatalog(country)
  const operator = useTourHost(Boolean(user))
  const enter = useEntrance(!isLoading && !error && (data?.length ?? 0) > 0)
  const handleCatalogScroll = useProductTelemetryScroll('tours.catalog.scroll')

  const countryName = useMemo(() => {
    const c = findCountry(country)
    if (!c) return country
    return i18n.language.startsWith('en') ? c.nameEn : c.nameEs
  }, [country, i18n.language])

  const openExperience = useCallback(
    (experienceId: string) => {
      router.push(`${resolvedRouteBase}/${experienceId}`)
    },
    [resolvedRouteBase, router],
  )

  const renderItem = useCallback(
    ({ item }: { item: TourCatalogExperience }) => (
      <TourCatalogCard experience={item} onPress={() => openExperience(item.id)} layout="compact" />
    ),
    [openExperience],
  )

  const isNational = useCallback(
    (item: TourCatalogExperience) =>
      (item.destinationCountryCode ?? item.originCountryCode ?? country) === country,
    [country],
  )
  const isRegional = useCallback(
    (item: TourCatalogExperience) =>
      item.destinationCountryCode != null && item.destinationCountryCode !== country,
    [country],
  )

  const categories = useMemo(() => {
    const map = new Map<string, string>()
    const isEnglish = i18n.language.startsWith('en')
    for (const item of data ?? []) {
      if (item.category && !NATIONAL_THEME_CATEGORY_CODES.has(item.category.code)) {
        map.set(item.category.code, isEnglish ? item.category.nameEn : item.category.nameEs)
      }
    }
    return Array.from(map, ([code, name]) => ({ code, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
  }, [data, i18n.language])

  const hasRegional = useMemo(() => (data ?? []).some(isRegional), [data, isRegional])

  const [selected, setSelected] = useState<string>('all')

  const selectCountry = useCallback(
    (nextCountry: string) => {
      setSelected('all')
      setCatalogCountry(nextCountry)
    },
    [setCatalogCountry],
  )

  const visibleExperiences = useMemo(() => {
    const list = data ?? []
    if (selected === 'all') return list
    if (selected === 'national') return list.filter(isNational)
    if (selected === 'regional') return list.filter(isRegional)
    return list.filter((item) => item.category?.code === selected)
  }, [data, selected, isNational, isRegional])

  const openHost = useCallback(() => {
    router.push(resolvedRouteBase === '/preview' ? '/preview/host' : '/(tabs)/tours/host')
  }, [resolvedRouteBase, router])

  const openBookings = useCallback(() => {
    router.push(resolvedRouteBase === '/preview' ? '/preview/bookings' : '/(tabs)/tours/bookings')
  }, [resolvedRouteBase, router])

  const openCustomQuote = useCallback(() => {
    router.push(`${resolvedRouteBase}/quote`)
  }, [resolvedRouteBase, router])

  if (!env.isToursEnabled) {
    return <Redirect href="/(tabs)/explore" />
  }

  const showFilterEmpty =
    !isLoading && !error && (data?.length ?? 0) > 0 && visibleExperiences.length === 0

  const header = (
    <View
      style={{
        paddingTop: SPACING.sm,
        paddingHorizontal: padX,
        flexDirection: 'row',
        alignItems: editorial ? 'center' : 'flex-start',
        gap: SPACING.md,
      }}
    >
      <View style={{ flex: 1, minWidth: 0, marginBottom: editorial ? 0 : SPACING.md }}>
        <Text
          style={{
            ...(editorial ? TEXT_STYLES.h2 : TEXT_STYLES.h1),
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
          testID="tours.catalog-title"
        >
          {t('tabs.tours')}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontFamily: FONTS.body,
            fontSize: editorial ? 14 : 15,
            color: colors.textSecondary,
          }}
          testID="tours.catalog-count"
        >
          {isLoading
            ? t('tours.catalog_subtitle', { country: countryName })
            : data && data.length > 0
              ? t('tours.catalog_count', { count: visibleExperiences.length })
              : t('tours.catalog_subtitle', { country: countryName })}
        </Text>
        {editorial ? null : (
          <View style={{ marginTop: SPACING.sm }}>
            <CountryPicker
              value={country}
              onChange={selectCountry}
              countries={CATALOG_COUNTRIES}
              testID="tours.catalog-country"
            />
          </View>
        )}
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
        {editorial ? (
          <CountryPicker
            value={country}
            onChange={selectCountry}
            countries={CATALOG_COUNTRIES}
            testID="tours.catalog-country"
          />
        ) : null}
        {operator.data?.host ? (
          <CatalogToolbarButton
            label={t('tours.catalog_host_action')}
            onPress={openHost}
            testID="tours.catalog-host"
            labeled={editorial}
          >
            <Building2 size={22} color={colors.textPrimary} strokeWidth={1.8} />
          </CatalogToolbarButton>
        ) : null}
        <CatalogToolbarButton
          label={t('tours.bookings.title')}
          onPress={openBookings}
          testID="tours.catalog-bookings"
          labeled={editorial}
        >
          <Receipt size={22} color={colors.textPrimary} strokeWidth={1.8} />
        </CatalogToolbarButton>
      </View>
    </View>
  )

  const filters =
    !isLoading && !error && (data?.length ?? 0) > 0 ? (
      <View style={{ paddingHorizontal: editorial ? padX : 0, paddingTop: SPACING.sm }}>
        <TourScopeFilter
          selected={selected}
          onSelect={setSelected}
          hasRegional={hasRegional}
          categories={categories}
          desktop={editorial}
          padded={!editorial}
        />
      </View>
    ) : null

  const customQuoteCta =
    country === 'SV' ? (
      <View
        style={{
          paddingHorizontal: padX,
          paddingTop: SPACING.md,
          // Keep the first catalog card from riding the CTA while the list
          // scrolls beneath this persistent catalog header block.
          paddingBottom: SPACING.sm,
        }}
      >
        <TourCustomQuoteCta onPress={openCustomQuote} />
      </View>
    ) : null

  const body = isLoading ? (
    editorial ? (
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: padX,
          paddingTop: SPACING.md,
          paddingBottom: SPACING['2xl'],
        }}
      >
        <ToursCatalogGridSkeleton columns={columns} gap={gap} />
      </ScrollView>
    ) : (
      <View style={{ paddingHorizontal: padX, paddingTop: SPACING.md, gap: SPACING.md }}>
        {[0, 1, 2].map((index) => (
          <TourCardSkeleton key={index} staggerIndex={index} />
        ))}
      </View>
    )
  ) : error ? (
    <View style={{ flex: 1, justifyContent: 'center' }}>
      <EmptyState
        illustration={<WifiOff size={44} color={colors.textMuted} strokeWidth={1.5} />}
        title={t('tours.catalog_error_title')}
        description={t('tours.catalog_error_description')}
        action={{ label: t('tours.retry'), onPress: () => void refetch() }}
      />
    </View>
  ) : !data || data.length === 0 ? (
    <View style={{ flex: 1, justifyContent: 'center' }}>
      <EmptyState
        illustration={<Search size={44} color={colors.textMuted} strokeWidth={1.5} />}
        title={t('tours.catalog_empty_country_title', { country: countryName })}
        description={t('tours.catalog_empty_country_description')}
        action={
          country === 'SV'
            ? undefined
            : { label: t('tours.catalog_view_el_salvador'), onPress: () => selectCountry('SV') }
        }
        testID="tours.catalog-empty"
      />
    </View>
  ) : showFilterEmpty ? (
    <View style={{ flex: 1, justifyContent: 'center' }}>
      <EmptyState
        illustration={<SearchX size={44} color={colors.textMuted} strokeWidth={1.5} />}
        title={t('tours.catalog_empty_filter_title')}
        description={t('tours.catalog_empty_filter_description')}
        action={{ label: t('tours.all'), onPress: () => setSelected('all') }}
        testID="tours.catalog-empty-filter"
      />
    </View>
  ) : editorial ? (
    <ScrollView
      style={{ flex: 1 }}
      showsVerticalScrollIndicator={false}
      onScroll={handleCatalogScroll}
      scrollEventThrottle={64}
      contentContainerStyle={{
        paddingHorizontal: padX,
        paddingTop: SPACING.md,
        paddingBottom: SPACING['2xl'],
      }}
      refreshControl={
        <BrandedRefreshControl refreshing={isRefetching} onRefresh={() => void refetch()} />
      }
    >
      <ToursCatalogGrid
        experiences={visibleExperiences}
        onOpen={openExperience}
        columns={columns}
        gap={gap}
        enter={enter}
      />
    </ScrollView>
  ) : (
    <FlashList
      data={visibleExperiences}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      ItemSeparatorComponent={() => <View style={{ height: SPACING.md }} />}
      contentContainerStyle={{
        paddingBottom: SPACING['2xl'],
        paddingTop: SPACING.md,
        paddingHorizontal: padX,
      }}
      style={{ flex: 1 }}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <BrandedRefreshControl refreshing={isRefetching} onRefresh={() => void refetch()} />
      }
    />
  )

  return (
    <ScreenContainer variant={editorial ? 'full-bleed' : undefined} scroll={false}>
      <View style={{ flex: 1 }}>
        {header}
        {filters}
        {customQuoteCta}
        {body}
      </View>
    </ScreenContainer>
  )
}

export default function ToursCatalogRoute() {
  return <ToursCatalogScreen />
}
